// Fleet filter contract §6.6 I3 — "outside" is scoped by OWNERSHIP, not by DOM containment.
//
// THE TRAP THIS EXISTS FOR (contract §1.3, and the reason §6.4 originally forbade outside-click
// dismissal at all): a filter panel that hosts a Fluent Dropdown hosts a PORTALLED listbox, which
// renders outside the panel's DOM subtree. Measured in this repo's own DOM, not assumed:
//
//   <body>
//     <div id="panel">…<button role="combobox" aria-controls="fluent-listboxr2">…</div>
//     <div class="fui-FluentProvider" data-portal-node="true">
//       <div id="fluent-listboxr2" role="listbox">…    <-- panel.contains(this) === FALSE
//
// So `panelRef.current.contains(e.target)` classifies a click on the panel's own option as
// "outside" and unmounts the panel mid-interaction. That is C24 failing, and it is exactly the
// naive implementation the contract rules out.
//
// THE MECHANISM: ARIA already IS the registry, and Fluent already maintains it. The combobox
// carries `aria-controls="<listbox id>"` and the Dropdown root carries `aria-owns="<listbox id>"`.
// Resolving those references from inside the panel gives the set of surfaces the panel owns,
// wherever they render. This is standards-based rather than a Fluent-internals hack, so it also
// covers any other correctly-authored portalled child (calendar, nested menu).

/** Attributes through which an element declares the surfaces it owns. */
const OWNERSHIP_ATTRS = ['aria-controls', 'aria-owns'] as const;

/**
 * Bounded because ownership can nest (a listbox that itself opens a surface) and a malformed or
 * circular set of references must not hang the page on every mousedown. Three levels is far more
 * than any filter surface needs; the visited-set already makes cycles safe, and this caps the
 * pathological breadth case too.
 */
const MAX_DEPTH = 3;

function ownedIdsOf(root: Element): string[] {
  const ids: string[] = [];
  const scan = (el: Element) => {
    for (const attr of OWNERSHIP_ATTRS) {
      const raw = el.getAttribute(attr);
      // Both attributes are ID-reference LISTS: space-separated, per ARIA.
      if (raw) for (const id of raw.split(/\s+/)) if (id) ids.push(id);
    }
  };
  scan(root);
  root.querySelectorAll(OWNERSHIP_ATTRS.map((a) => `[${a}]`).join(',')).forEach(scan);
  return ids;
}

/**
 * Is `target` inside one of `roots`, or inside any surface those roots (transitively) own?
 *
 * @param roots The trigger and the panel. Nulls are tolerated so callers can pass refs directly
 *   without a guard at every site.
 */
export function isInsideOwnedSurface(target: Node | null, roots: (Element | null | undefined)[]): boolean {
  if (!target) return false;

  const frontier: Element[] = roots.filter((r): r is Element => !!r);
  const visited = new Set<Element>(frontier);

  for (let depth = 0; depth <= MAX_DEPTH && frontier.length > 0; depth += 1) {
    for (const root of frontier) {
      if (root === target || root.contains(target)) return true;
    }

    const next: Element[] = [];
    for (const root of frontier) {
      for (const id of ownedIdsOf(root)) {
        // getElementById rather than a selector: ids are not guaranteed to be valid CSS
        // identifiers, and Fluent's generated ids have burned people on exactly that before.
        const owned = root.ownerDocument?.getElementById(id);
        if (owned && !visited.has(owned)) {
          visited.add(owned);
          next.push(owned);
        }
      }
    }
    frontier.length = 0;
    frontier.push(...next);
  }

  return false;
}
